import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Check, X, Image, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../App';
import InstagramShareSheet from './InstagramShareSheet';

/**
 * PostJoinShareModal - Shown immediately after joining a run
 * 
 * "You're in. Need to bring your guys?"
 * 
 * Buttons:
 * - Share Run
 * - Copy Invite
 * - Dismiss
 */
const PostJoinShareModal = ({ 
  open, 
  onOpenChange, 
  run 
}) => {
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [instagramOpen, setInstagramOpen] = useState(false);
  const [instagramPreview, setInstagramPreview] = useState(null);
  const [instagramLoading, setInstagramLoading] = useState(false);
  const [clickableShareBusy, setClickableShareBusy] = useState(false);
  const runData = run || {};
  const runId = runData.id;
  
  // Format time: "7pm"
  const formatTimeShort = (dateStr, tz) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        hour12: true,
        timeZone: tz || undefined,
      }).toLowerCase().replace(' ', '');
    } catch {
      return '';
    }
  };
  
  // Calculate spots left
  const playerCount = (runData.team_a_players?.length || 0) + (runData.team_b_players?.length || 0);
  const maxPlayers = runData.max_players || 10;
  const spotsLeft = Math.max(0, maxPlayers - playerCount);
  
  // Build share link
  const fallbackShareLink = runId ? `https://openrunsusa.com/run/${runId}` : '';
  const publicShareMessage = String(runData.public_share_message || '').trim();
  // GameDetail renders description as host-authored run copy; use it only after explicit public share fields.
  const publicDescriptionMessage = String(runData.description || '').trim();
  const courtName = runData.location_name || 'the court';

  const formatDateForShare = (dateStr, tz) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: tz || undefined,
      });
    } catch {
      return '';
    }
  };

  const getPreviewPublicMessage = (preview) => {
    if (preview && Object.prototype.hasOwnProperty.call(preview, 'public_share_message')) {
      const previewMessage = String(preview.public_share_message || '').trim();
      if (previewMessage) return previewMessage;
    }
    return publicShareMessage || publicDescriptionMessage;
  };

  const formatAvailability = (spotsOpen, waitlistEnabled = runData.waitlist_enabled) => {
    if (spotsOpen > 0) {
      return `${spotsOpen} ${spotsOpen === 1 ? 'spot open' : 'spots open'}`;
    }
    return waitlistEnabled ? 'Waitlist open' : 'Currently full';
  };

  const buildShareMessage = (preview) => {
    const shareLink = preview?.share_url || fallbackShareLink;
    const previewMessage = getPreviewPublicMessage(preview);
    const title = String(preview?.public_title || preview?.title || runData.title || '').trim();
    const dateLabel = formatDateForShare(preview?.date_time || runData.date_time, preview?.tz || runData.tz);
    const timeLabel = formatTimeShort(preview?.date_time || runData.date_time, preview?.tz || runData.tz);
    const locationLabel = preview?.public_location_label || preview?.city || runData.city || courtName;
    const previewSpotsLeft = preview?.spots_left ?? spotsLeft;
    const availability = formatAvailability(previewSpotsLeft, preview?.waitlist_enabled ?? runData.waitlist_enabled);
    const lines = [];

    if (previewMessage) {
      lines.push(previewMessage, '');
    }

    if (title) lines.push(title);
    if (dateLabel || timeLabel) lines.push([dateLabel, timeLabel].filter(Boolean).join(' at '));
    if (locationLabel) lines.push(locationLabel);
    if (availability) lines.push(availability);

    if (shareLink) {
      lines.push('', 'Join here:', shareLink);
    }

    return lines.join('\n');
  };

  const shareMessage = buildShareMessage(instagramPreview);

  const loadPublicPreview = async () => {
    if (!runId) return null;
    if (instagramPreview) return instagramPreview;
    const response = await apiClient.get(`/runs/${runId}/preview`);
    setInstagramPreview(response.data);
    return response.data;
  };

  useEffect(() => {
    if (!open || !runId || instagramPreview) return;
    let cancelled = false;

    const loadPreview = async () => {
      try {
        const response = await apiClient.get(`/runs/${runId}/preview`);
        if (!cancelled) {
          setInstagramPreview(response.data);
        }
      } catch {
        // Fall back to the authenticated run data already available.
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [open, runId, instagramPreview]);

  if (!run) return null;

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: run.tz || undefined,
      });
    } catch {
      return '';
    }
  };

  const getInstagramData = () => {
    const preview = instagramPreview || {};
    const publicType = preview.public_type === 'training' ? 'training' : 'run';
    const typeLabel = publicType === 'training' ? 'Training' : 'Open Run';
    const locationLabel = preview.public_location_label || preview.city || 'Location shared after joining';
    const rawTitle = String(preview.public_title || preview.title || '').trim();
    const genericTitles = new Set(['', 'pickup run', 'open run', 'run', 'training', 'basketball training']);
    const hasCustomTitle = rawTitle && !genericTitles.has(rawTitle.toLowerCase());
    const headline = hasCustomTitle
      ? rawTitle
      : (locationLabel === 'Location shared after joining' ? typeLabel : `${typeLabel} at ${locationLabel}`);
    const previewSpotsLeft = preview.spots_left ?? spotsLeft;
    const joinedCount = Math.max(0, (preview.max_players || maxPlayers) - previewSpotsLeft);
    const spotsLabel = formatAvailability(previewSpotsLeft, preview.waitlist_enabled ?? run.waitlist_enabled);

    return {
      headline,
      typeLabel,
      dateLabel: formatDateLong(preview.date_time || run.date_time),
      timeLabel: formatTimeShort(preview.date_time || run.date_time, preview.tz || run.tz),
      locationLabel,
      capacityLabel: `${joinedCount} of ${preview.max_players || maxPlayers} joined`,
      spotsLabel,
      shareUrl: preview.share_url || fallbackShareLink,
      publicShareMessage: getPreviewPublicMessage(preview),
      publicType
    };
  };
  
  // Track share event
  const trackEvent = async (eventType) => {
    try {
      await apiClient.post('/share/events', {
        event_type: eventType,
        run_id: run.id,
        source: 'post_join',
      });
    } catch (e) {
      // Silent fail
    }
  };

  const handleInstagramShare = async () => {
    setInstagramLoading(true);
    try {
      await loadPublicPreview();
      setInstagramOpen(true);
      trackEvent('share_image_sheet_opened');
    } catch {
      toast.error('Could not load share preview');
    } finally {
      setInstagramLoading(false);
    }
  };
  
  // Handle copy
  const handleCopy = async () => {
    let message = shareMessage;
    try {
      message = buildShareMessage(await loadPublicPreview());
    } catch {
      // Fall back to the local run object if preview loading fails.
    }
    
    try {
      await navigator.clipboard.writeText(message);
      trackEvent('invite_text_copied');
      setCopied(true);
      toast.success('Invite text copied');
      setTimeout(() => setCopied(false), 2000);
      setTimeout(() => onOpenChange(false), 500);
    } catch (err) {
      toast.error("Couldn't copy automatically. Please copy manually.");
    }
  };

  const handleCopyLink = async () => {
    let link = fallbackShareLink;
    try {
      const preview = await loadPublicPreview();
      link = preview?.share_url || fallbackShareLink;
    } catch {
      // Fall back to the public run URL if preview loading fails.
    }

    try {
      await navigator.clipboard.writeText(link);
      trackEvent('join_link_copied');
      setLinkCopied(true);
      toast.success('Join link copied');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy automatically. Please copy manually.");
    }
  };

  const handleSendClickableInvite = async () => {
    setClickableShareBusy(true);
    let preview = instagramPreview;
    try {
      preview = await loadPublicPreview();
    } catch {
      // Fall back to the authenticated run data already available.
    }

    const message = buildShareMessage(preview);
    const link = preview?.share_url || fallbackShareLink;

    try {
      if (navigator.share) {
        await navigator.share({
          title: runData.title || 'OpenRuns invite',
          text: message,
          url: link || undefined,
        });
        trackEvent('clickable_invite_shared');
        return;
      }

      await navigator.clipboard.writeText(message);
      trackEvent('invite_text_copied');
      toast.success('Invite text copied');
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error("Couldn't share automatically. Copy the invite text instead.");
      }
    } finally {
      setClickableShareBusy(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-md mx-4 rounded-2xl p-6"
        data-testid="post-join-share-modal"
      >
        {/* Close button */}
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 p-1 rounded-full hover:bg-zinc-100 transition-colors"
          data-testid="dismiss-share-modal"
        >
          <X className="w-5 h-5 text-zinc-400" />
        </button>
        
        {/* Header */}
        <div className="text-center mb-6 pt-2">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-zinc-900 mb-1">
            You're in
          </h2>
          <p className="text-zinc-500">
            Need to bring your guys?
          </p>
        </div>

        <div className="mb-5 rounded-2xl border border-zinc-200 bg-white p-4 text-left shadow-sm">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Invite preview
          </p>
          <p className="whitespace-pre-line text-sm leading-6 text-zinc-800">
            {shareMessage}
          </p>
        </div>

        <p className="mb-5 rounded-2xl bg-orange-50 p-4 text-sm leading-6 text-left text-orange-950">
          Send a clickable invite for messages and DMs. Instagram images are not clickable; for Stories, add the copied link as a Link Sticker.
        </p>
        
        {/* Action buttons */}
        <div className="space-y-3">
          <Button
            onClick={handleSendClickableInvite}
            disabled={clickableShareBusy}
            className="w-full h-12 rounded-xl font-semibold bg-[#F97316] hover:bg-[#EA580C] text-white gap-2"
            data-testid="send-clickable-invite-btn"
          >
            <Share2 className={`w-4 h-4 ${clickableShareBusy ? 'animate-pulse' : ''}`} />
            Send Clickable Invite
          </Button>

          <Button
            onClick={handleInstagramShare}
            disabled={instagramLoading}
            variant="outline"
            className="w-full h-12 rounded-xl font-semibold gap-2"
            data-testid="share-image-btn"
          >
            <Image className={`w-4 h-4 ${instagramLoading ? 'animate-pulse' : ''}`} />
            Share Story Image + Copy Link
          </Button>

          <Button
            onClick={handleCopy}
            variant="outline"
            className="w-full h-12 rounded-xl font-semibold gap-2"
            data-testid="copy-invite-btn"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Copy Invite Text
          </Button>
          
          {/* Copy button */}
          <Button
            onClick={handleCopyLink}
            variant="outline"
            className="w-full h-12 rounded-xl font-semibold gap-2"
            data-testid="copy-join-link-btn"
          >
            {linkCopied ? (
              <>
                <Check className="w-4 h-4 text-green-500" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy Join Link
              </>
            )}
          </Button>
          
          {/* Dismiss */}
          <Button
            onClick={() => onOpenChange(false)}
            variant="ghost"
            className="w-full h-12 rounded-xl font-medium text-zinc-500 hover:text-zinc-700"
            data-testid="dismiss-btn"
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
      <InstagramShareSheet
        open={instagramOpen}
        onOpenChange={setInstagramOpen}
        {...getInstagramData()}
        onTrack={trackEvent}
      />
    </Dialog>
  );
};

export default PostJoinShareModal;
